import { rooms } from "@station/contracts";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useWashPermissions, useWashQueue, useWashStations } from "../../features/wash/queries.js";
import { QueueBoard } from "../../features/wash/queue-board.js";
import { formatClock } from "../../features/wash/wash-values.js";
import { errorMessage } from "../../lib/errors.js";
import { useDocumentTitle } from "../../lib/hooks.js";
import { useRoom } from "../../lib/realtime-context.js";

const searchSchema = z.object({ stationId: z.string().uuid().optional().catch(undefined) });

export const Route = createFileRoute("/sira-ekrani/yikama")({
  validateSearch: (search) => searchSchema.parse(search),
  component: WashQueueScreen,
});

/**
 * Yıkama sıra ekranı — TV / duvar ekranı (ADR 0018 §14): kabuksuz, büyük ve yüksek kontrastlı yazı, maskeli plaka ve iş
 * emri numarası, tahmini hazır olma saati; ad / telefon YOK. İstasyon odasındaki `wash.work_order.status_changed`
 * olayında ve ayrıca 30 sn'de bir tazelenir; bağlantı koparsa son bilinen liste kalır ve saat güncellenmeye devam eder.
 */
function WashQueueScreen(): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const permissions = useWashPermissions();
  const stations = useWashStations(permissions.enabled && permissions.canRead);
  const station =
    stations.data?.find((candidate) => candidate.id === search.stationId) ??
    (search.stationId ? null : (stations.data?.[0] ?? null));
  const stationId = station?.id ?? search.stationId ?? null;
  const queue = useWashQueue(permissions.enabled && permissions.canRead ? stationId : null);
  useRoom(stationId ? rooms.station(stationId) : null);
  useDocumentTitle(t("wash.queue.tvTitle", { station: station?.name ?? "" }));
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const handle = setInterval(() => setNow(new Date()), 10_000);
    return () => clearInterval(handle);
  }, []);

  const message = !permissions.enabled
    ? t("wash.disabledTitle")
    : !permissions.canRead
      ? t("wash.noPermission.read")
      : queue.error
        ? errorMessage(queue.error)
        : stations.isSuccess && stationId === null
          ? t("wash.noStationsTitle")
          : null;

  return (
    <div className="min-h-dvh bg-neutral-950 p-6 text-white">
      <main className="mx-auto grid max-w-[1800px] gap-6">
        <header className="flex flex-wrap items-baseline justify-between gap-4">
          <h1 className="text-4xl font-bold">
            {t("wash.queue.tvHeading")}
            {station ? (
              <span className="ms-4 text-3xl font-semibold text-neutral-200">{station.name}</span>
            ) : null}
          </h1>
          <p className="text-4xl font-semibold tabular">
            <span className="sr-only">{t("wash.queue.clock")}: </span>
            {formatClock(now.toISOString())}
          </p>
        </header>
        {message ? (
          <p role="alert" className="rounded-lg bg-neutral-900 p-6 text-2xl">
            {message}
          </p>
        ) : queue.data ? (
          <QueueBoard queue={queue.data} variant="tv" />
        ) : (
          <p className="text-2xl text-neutral-300">{t("common.loading")}</p>
        )}
        <footer className="text-lg text-neutral-300">{t("wash.queue.tvFooter")}</footer>
      </main>
    </div>
  );
}
